/* Mocked in-memory implementation of Workflows Client for local development and UI wiring. */

import { IWorkflowsClient } from './IWorkflowsClient';
import {
  WorkflowTriggerDto,
  WorkflowConditionFieldDto,
  WorkflowActionDto,
  AutomatedWorkflowDto,
  CreateUpdateWorkflowDto,
  WorkflowTriggerType,
  WorkflowUserDto,
  WorkflowRoleDto,
  WorkflowOrderTypeDto,
  WorkflowSupplierDto,
  WorkflowStockListDto,
  WorkflowZoneDto,
  WorkflowReportTypeDto,
  WorkflowTaskTemplateDto,
} from './workflowsTypes';
import {
  mockTriggers,
  mockConditionFields,
  mockActions,
  mockWorkflows,
  mockUsers,
  mockRoles,
  mockOrderTypes,
  mockSuppliers,
  mockStockLists,
  mockZones,
  mockReportTypes,
  mockTaskTemplates,
  idGenerators,
} from './workflowsMockData';

/* ---- Workflows Client (Mocked) ---- */

export class WorkflowsClientMocked implements IWorkflowsClient {
  async getTriggers(): Promise<WorkflowTriggerDto[]> {
    return Promise.resolve([...mockTriggers]);
  }

  async getConditionFields(): Promise<WorkflowConditionFieldDto[]> {
    return Promise.resolve([...mockConditionFields]);
  }

  /**
   * Get condition fields filtered by the selected trigger type.
   * Returns only fields that are applicable to the trigger, plus generic fields.
   */
  async getConditionFieldsForTrigger(triggerType: WorkflowTriggerType): Promise<WorkflowConditionFieldDto[]> {
    const filtered = mockConditionFields.filter(field => {
      // If no applicable triggers specified, the field applies to all triggers
      if (!field.applicableTriggers || field.applicableTriggers.length === 0) {
        return true;
      }
      // Otherwise, check if the trigger is in the applicable list
      return field.applicableTriggers.includes(triggerType);
    });
    return Promise.resolve([...filtered]);
  }

  async getActions(): Promise<WorkflowActionDto[]> {
    return Promise.resolve([...mockActions]);
  }

  async getWorkflows(): Promise<AutomatedWorkflowDto[]> {
    return Promise.resolve([...mockWorkflows]);
  }

  async getWorkflow(id: number): Promise<AutomatedWorkflowDto | null> {
    const workflow = mockWorkflows.find(w => w.id === id);
    return Promise.resolve(workflow ? { ...workflow } : null);
  }

  async createWorkflow(dto: CreateUpdateWorkflowDto): Promise<AutomatedWorkflowDto> {
    const newWorkflow: AutomatedWorkflowDto = {
      id: idGenerators.nextWorkflowId++,
      ...dto,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1,
      updatedAtUtc: null,
      updatedByUserId: null,
      lastTriggeredAtUtc: null,
      triggerCount: 0,
    };
    mockWorkflows.push(newWorkflow);
    return Promise.resolve({ ...newWorkflow });
  }

  async updateWorkflow(id: number, dto: CreateUpdateWorkflowDto): Promise<AutomatedWorkflowDto> {
    const index = mockWorkflows.findIndex(w => w.id === id);
    if (index === -1) throw new Error('Workflow not found');

    const updated: AutomatedWorkflowDto = {
      ...mockWorkflows[index],
      ...dto,
      updatedAtUtc: new Date().toISOString(),
      updatedByUserId: 1,
    };
    mockWorkflows[index] = updated;
    return Promise.resolve({ ...updated });
  }

  async deleteWorkflow(id: number): Promise<void> {
    const index = mockWorkflows.findIndex(w => w.id === id);
    if (index !== -1) {
      mockWorkflows.splice(index, 1);
    }
    return Promise.resolve();
  }

  async toggleWorkflowActive(id: number, isActive: boolean): Promise<AutomatedWorkflowDto> {
    const index = mockWorkflows.findIndex(w => w.id === id);
    if (index === -1) throw new Error('Workflow not found');

    mockWorkflows[index] = {
      ...mockWorkflows[index],
      isActive,
      updatedAtUtc: new Date().toISOString(),
      updatedByUserId: 1,
    };
    return Promise.resolve({ ...mockWorkflows[index] });
  }

  /* ---- Reference Data Methods ---- */

  async getUsers(): Promise<WorkflowUserDto[]> {
    return Promise.resolve([...mockUsers]);
  }

  async getRoles(): Promise<WorkflowRoleDto[]> {
    return Promise.resolve([...mockRoles]);
  }

  async getOrderTypes(): Promise<WorkflowOrderTypeDto[]> {
    return Promise.resolve([...mockOrderTypes]);
  }

  async getSuppliers(): Promise<WorkflowSupplierDto[]> {
    return Promise.resolve([...mockSuppliers]);
  }

  async getStockLists(): Promise<WorkflowStockListDto[]> {
    return Promise.resolve([...mockStockLists]);
  }

  async getZones(): Promise<WorkflowZoneDto[]> {
    return Promise.resolve([...mockZones]);
  }

  async getReportTypes(): Promise<WorkflowReportTypeDto[]> {
    return Promise.resolve([...mockReportTypes]);
  }

  async getTaskTemplates(): Promise<WorkflowTaskTemplateDto[]> {
    return Promise.resolve([...mockTaskTemplates]);
  }
}
